(() => {
  const sections = document.querySelectorAll('[data-pricing-plan-section]');
  const forms = document.querySelectorAll('[data-launch-checkout-form]');

  function setActivePlan(section, plan) {
    const nextPlan = plan || section.dataset.defaultPlan || 'monthly';
    const showAllCards = section.classList.contains('pricing-plan--2026') && window.matchMedia('(min-width: 990px)').matches;

    section.querySelectorAll('[data-plan-tab]').forEach((tab) => {
      const isActive = tab.dataset.planTab === nextPlan;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    section.querySelectorAll('[data-plan-card]').forEach((card) => {
      card.hidden = showAllCards ? false : card.dataset.planCard !== nextPlan;
    });
  }

  sections.forEach((section) => {
    setActivePlan(section, section.dataset.defaultPlan);

    section.querySelectorAll('[data-plan-tab]').forEach((tab) => {
      tab.addEventListener('click', () => setActivePlan(section, tab.dataset.planTab));
    });

    window.addEventListener('resize', () => {
      const activeTab = section.querySelector('[data-plan-tab].is-active');
      setActivePlan(section, activeTab?.dataset.planTab || section.dataset.defaultPlan);
    });
  });

  if (!forms.length) return;

  const shopDomain = window.Shopify?.shop || window.location.hostname;
  const token = window.theme?.storefrontApiToken;
  const apiVersion = window.theme?.storefrontApiVersion || '2026-01';
  const apiUrl = `https://${shopDomain}/api/${apiVersion}/graphql.json`;

  const CART_CREATE_MUTATION = `
    mutation CreateCheckoutCart($input: CartInput!, $language: LanguageCode) @inContext(language: $language) {
      cartCreate(input: $input) {
        cart {
          checkoutUrl
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  function getSubmitButton(form) {
    return form.querySelector('button[type="submit"]');
  }

  function resetCheckoutButtons() {
    forms.forEach((form) => {
      const button = getSubmitButton(form);

      // Back/forward cache can restore the disabled state after checkout navigation.
      if (button?.dataset.launchCanSubmit === 'true') {
        button.disabled = false;
      }
    });
  }

  function gid(type, id) {
    const value = String(id || '').trim();
    if (!value) return '';
    return value.indexOf('gid://') === 0 ? value : `gid://shopify/${type}/${value}`;
  }

  function idFromGid(id) {
    return String(id || '').split('/').pop();
  }

  function normalizeCountryCode(value) {
    const countryCode = String(value || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(countryCode) ? countryCode : '';
  }

  function normalizeLanguageCode(value) {
    const languageCode = String(value || '').trim().replace(/-/g, '_').toUpperCase();
    return /^[A-Z]{2,3}(?:_[A-Z]{2})?$/.test(languageCode) ? languageCode : '';
  }

  function getCheckoutContext(form) {
    const section = form.closest('[data-pricing-plan-section]');

    if (!section) return null;

    const cartAddUrl = section.dataset.cartAddUrl;
    const cartClearUrl = section.dataset.cartClearUrl;
    const checkoutUrl = section.dataset.checkoutUrl;

    if (!cartAddUrl || !cartClearUrl || !checkoutUrl) return null;

    return {
      cartAddUrl,
      cartClearUrl,
      checkoutUrl,
      countryCode: normalizeCountryCode(section.dataset.countryCode),
      languageCode: normalizeLanguageCode(section.dataset.languageCode),
    };
  }

  function sortCheckoutLines(lines) {
    const orderedLines = lines
      .map((line, index) => ({ ...line, index }))
      .sort((a, b) => {
        const aPriority = a.checkoutRole === 'subscription' || a.sellingPlanId ? 0 : 1;
        const bPriority = b.checkoutRole === 'subscription' || b.sellingPlanId ? 0 : 1;

        return aPriority === bPriority ? a.index - b.index : aPriority - bPriority;
      })
      .map(({ index, ...line }) => line);

    // Shopify checkout shows mixed cart lines in reverse handoff order.
    if (orderedLines.length > 1) {
      return orderedLines.reverse();
    }

    return orderedLines;
  }

  function toStorefrontLine(line) {
    const cartLine = {
      merchandiseId: line.merchandiseId,
      quantity: line.quantity,
    };

    if (line.sellingPlanId) {
      cartLine.sellingPlanId = line.sellingPlanId;
    }

    return cartLine;
  }

  function toAjaxCartItem(line) {
    const item = {
      id: idFromGid(line.merchandiseId),
      quantity: line.quantity,
    };

    if (line.sellingPlanId) {
      item.selling_plan = idFromGid(line.sellingPlanId);
    }

    return item;
  }

  function getLines(form) {
    const configuredLines = Array.from(form.querySelectorAll('[data-launch-line]'))
      .map((line) => {
        const variantId = line.dataset.variantId;
        const sellingPlanId = line.dataset.sellingPlanId;
        const quantity = Number(line.dataset.quantity || 1);
        const checkoutRole = line.dataset.checkoutRole || '';

        if (!variantId) return null;

        const cartLine = {
          checkoutRole,
          merchandiseId: gid('ProductVariant', variantId),
          quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        };

        if (sellingPlanId) {
          cartLine.sellingPlanId = gid('SellingPlan', sellingPlanId);
        }

        return cartLine;
      })
      .filter(Boolean);

    if (configuredLines.length) return sortCheckoutLines(configuredLines);

    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = Number(formData.get('quantity') || 1);
    const sellingPlanId = formData.get('selling_plan');

    if (!variantId) return [];

    const fallbackLine = {
      merchandiseId: gid('ProductVariant', variantId),
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    };

    if (sellingPlanId) {
      fallbackLine.sellingPlanId = gid('SellingPlan', sellingPlanId);
    }

    return sortCheckoutLines([fallbackLine]);
  }

  async function createCart(lines, context) {
    if (!token) throw new Error('Missing Storefront API token.');

    const input = {
      lines: lines.map(toStorefrontLine),
    };

    if (context.countryCode) {
      input.buyerIdentity = {
        countryCode: context.countryCode,
      };
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query: CART_CREATE_MUTATION,
        variables: {
          input,
          language: context.languageCode || null,
        },
      }),
    });

    if (!response.ok) throw new Error('Storefront API request failed.');

    const payload = await response.json();
    const userErrors = payload.data?.cartCreate?.userErrors || [];
    const checkoutUrl = payload.data?.cartCreate?.cart?.checkoutUrl;

    if (payload.errors?.length || userErrors.length || !checkoutUrl) {
      throw new Error('Unable to create checkout cart.');
    }

    return checkoutUrl;
  }

  async function clearCart(cartClearUrl) {
    const response = await fetch(cartClearUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) throw new Error('Cart clear request failed.');
  }

  async function addLineToCart(line, cartAddUrl) {
    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(toAjaxCartItem(line)),
    });

    if (!response.ok) throw new Error('Cart add request failed.');
  }

  async function replaceCartAndCheckout(lines, context) {
    await clearCart(context.cartClearUrl);

    for (const line of lines) {
      await addLineToCart(line, context.cartAddUrl);
    }

    return context.checkoutUrl;
  }

  forms.forEach((form) => {
    const initialButton = getSubmitButton(form);

    if (initialButton && !initialButton.disabled) {
      initialButton.dataset.launchCanSubmit = 'true';
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const button = getSubmitButton(form);
      const lines = getLines(form);
      const context = getCheckoutContext(form);

      if (!lines.length || !context || button?.disabled) return;

      if (button) button.disabled = true;

      try {
        window.location.href = token ? await createCart(lines, context) : await replaceCartAndCheckout(lines, context);
      } catch (error) {
        try {
          window.location.href = await replaceCartAndCheckout(lines, context);
        } catch (fallbackError) {
          if (button) button.disabled = false;
        }
      }
    });
  });

  window.addEventListener('pageshow', resetCheckoutButtons);
})();
